import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheckCircle, faSync, faCheck, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { authService } from '../../services/auth.service';
import logoElpaketColor from '../../assets/images/logo-elpaket-color.png';
import styles from '../../styles/common/UnverifiedAccountModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';

interface UnverifiedAccountModalProps {
    show: boolean;
    onClose: () => void;
    email?: string;
}

const UnverifiedAccountModal: React.FC<UnverifiedAccountModalProps> = ({
    show,
    onClose,
    email,
}) => {
    const [resending, setResending] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [resendMessage, setResendMessage] = useState<string | null>(null);
    const [resendError, setResendError] = useState<string | null>(null);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    // Reset state when modal is shown or hidden
    useEffect(() => {
        if (!show) {
            setResendMessage(null);
            setResendError(null);
            setCountdown(0);
            setResending(false);
        }
    }, [show]);

    const handleResend = async () => {
        if (!email) return;
        setResending(true);
        setResendMessage(null);
        setResendError(null);

        const result = await authService.resendVerificationEmail(email);
        setResending(false);

        if (result.success) {
            setResendMessage('¡Correo de verificación reenviado con éxito!');
            setCountdown(60); // 60 seconds cooldown
        } else {
            setResendError(result.error || 'Ocurrió un error al reenviar el correo.');
        }
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            className={styles.unverifiedAccountModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header className={styles.modalHeaderCustom}>
                <Modal.Title as="h3" className={styles.modalTitleCustom}>
                    <FontAwesomeIcon icon={faEnvelope} className={styles.iconCustom} />
                    Verifica tu cuenta
                </Modal.Title>
                <button type="button" className={styles.customCloseButton} onClick={onClose} aria-label="Close">
                    ×
                </button>
            </Modal.Header>

            <Modal.Body className={styles.modalBodyCustom}>
                <div className={styles.content}>
                    {/* Custom Yellow Icon with Sparks */}
                    <div className={styles.customSwalIconWrapper}>
                        <div className={`${styles.spark} ${styles.spark1}`}></div>
                        <div className={`${styles.spark} ${styles.spark2}`}></div>
                        <div className={`${styles.spark} ${styles.spark3}`}></div>
                        <div className={`${styles.spark} ${styles.spark4}`}></div>
                        <div className={styles.customSwalIcon}>✓</div>
                    </div>
                    
                    <h4 className={styles.title}>
                        ¡Casi <span className={styles.highlight}>listo!</span>
                    </h4>
                    
                    <p className={styles.message}>
                        Para completar el registro, necesitas verificar tu dirección de correo electrónico.
                    </p>
                    
                    {email && (
                        <div className={styles.emailContainerCard}>
                            <div className={styles.emailIconCircle}>
                                <FontAwesomeIcon icon={faEnvelope} className={styles.emailCardIcon} />
                            </div>
                            <div className={styles.emailTextWrapper}>
                                Hemos enviado un enlace de verificación a:<br />
                                <strong>{email}</strong>
                            </div>
                        </div>
                    )}

                    <div className={styles.resendWrapperCustom}>
                        <span className={styles.resendLabelCustom}>¿No recibiste el correo?</span>
                        <button
                            onClick={handleResend}
                            className={styles.resendBtnCustom}
                            disabled={resending || countdown > 0}
                        >
                            {resending ? 'Reenviando...' : countdown > 0 ? `Reenviar en ${countdown}s` : 'Reenviar correo'}
                            <FontAwesomeIcon icon={faSync} className={resending ? 'fa-spin' : ''} style={{ marginLeft: '8px' }} />
                        </button>
                        {resendMessage && (
                            <div className={styles.resendSuccessCustom}>
                                {resendMessage}
                            </div>
                        )}
                        {resendError && (
                            <div className={styles.resendErrorCustom}>
                                {resendError}
                            </div>
                        )}
                    </div>

                    <div className={styles.instructionsCustom}>
                        <div className={styles.instructionsBulbWrapper}>
                            <FontAwesomeIcon icon={faLightbulb} className={styles.bulbIcon} />
                        </div>
                        <div className={styles.instructionsTextWrapper}>
                            <p className={styles.instructionsHeading}>Por favor:</p>
                            <ul className={styles.instructionsList}>
                                <li>
                                    <FontAwesomeIcon icon={faCheck} className={styles.bulletCheck} />
                                    Revisa tu bandeja de entrada
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faCheck} className={styles.bulletCheck} />
                                    Revisa tu carpeta de spam o correos no deseados
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faCheck} className={styles.bulletCheck} />
                                    Haz clic en el enlace de verificación
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Entendido button centered inside body */}
                    <button 
                        onClick={onClose}
                        className={styles.understandBtnCustom}
                    >
                        Entendido
                    </button>

                    {/* Logo at the bottom */}
                    <div className={styles.bottomLogoWrapper}>
                        <img src={logoElpaketColor} className={styles.bottomLogo} alt="elPaket" />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default UnverifiedAccountModal;
